<template>
	<article
		class="activity-card"
		:class="[`type-${item.type}`, { clickable: isCardClickable }]"
		@click="onCardClick"
	>
		<header class="activity-card-header">
			<div class="pills">
				<ActivityTypePill :type="item.type" />
				<ActivityTypePill
					v-if="threadName"
					variant="thread"
					:label="$t('admin_activity_pill_thread', { ThreadName: threadName })"
				/>
				<ActivityTypePill v-if="isDeleted" variant="deleted" />
			</div>
			<time class="activity-timestamp" v-text="formattedTime" />
		</header>
		<div class="activity-card-body">
			<component :is="bodyComponent" :item="item" :user="user" />
		</div>
		<footer
			v-if="showFooter"
			class="activity-card-footer"
			:class="{ 'has-text': wordCount > 0 }"
		>
			<span v-if="wordCount" class="word-count">
				{{
					$t(
						'admin_activity_word_count.variable',
						{ Count: wordCount },
						wordCount
					)
				}}
			</span>
			<a
				v-if="chatUrl && !isDeleted"
				class="show-in-chat"
				:href="chatUrl"
				target="_blank"
				rel="noopener noreferrer"
				@click="onShowInChat"
			>
				{{ $t('admin_activity_show_in_chat') }}
			</a>
		</footer>
	</article>
</template>

<script>
import { getChat } from '@/store'
import ActivityTypePill from './ActivityTypePill'
import ActivityMessageBody from './ActivityMessageBody'
import ActivityReactionBody from './ActivityReactionBody'
import ActivityPollResponseBody from './ActivityPollResponseBody'
import ActivityEventBody from './ActivityEventBody'

export default {
	name: 'ActivityCard',

	components: { ActivityTypePill },

	props: {
		item: { type: Object, required: true },
		chatId: { type: [Number, String], default: null },
		user: { type: Object, default: null },
	},

	emits: ['select-message'],

	methods: {
		onShowInChat(event) {
			// Let cmd/ctrl/middle-click open the chat in a new tab (the
			// fallback href). Plain click opens the in-page preview.
			if (this.isModifiedClick(event)) return
			// Poll-only items have no messageId so the preview can't open;
			// fall through to the href + target=_blank to open the chat in
			// a new tab.
			if (!this.previewTarget) return
			event.preventDefault()
			this.$emit('select-message', this.previewTarget)
		},

		onCardClick(event) {
			// Interactive bits inside the card (the "show in chat" link,
			// mentions, the translation toggle, media that opens the lightbox)
			// handle their own clicks — don't double-fire when one of those is
			// the target.
			if (event.target.closest('a, button, .pointer, .message-media-container'))
				return
			// Don't hijack a text selection (admin copying message text).
			const selection = window.getSelection()
			if (selection && !selection.isCollapsed) return
			if (this.isModifiedClick(event)) return
			if (!this.isCardClickable) return
			if (this.previewTarget) {
				this.$emit('select-message', this.previewTarget)
				return
			}
			// Poll-only items have no message to preview — open the chat in a
			// new tab, matching the "show in chat" link's fallback.
			window.open(this.chatUrl, '_blank', 'noopener,noreferrer')
		},

		isModifiedClick(event) {
			return (
				event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1
			)
		},
	},

	computed: {
		bodyComponent() {
			switch (this.item.type) {
				case 'reaction':
					return ActivityReactionBody
				case 'poll_response':
					return ActivityPollResponseBody
				case 'chat_event':
					return ActivityEventBody
				case 'message':
				case 'reply':
				default:
					return ActivityMessageBody
			}
		},

		message() {
			return this.item.message
		},

		// Mirror the "show in chat" link's visibility: a card is clickable
		// whenever that link would render (has a target chat, not deleted).
		// Items with a message open the in-page preview; poll-only items
		// fall back to opening the chat in a new tab.
		isCardClickable() {
			return !!this.chatUrl && !this.isDeleted
		},

		// The chat + message the in-page preview opens to, or null when the
		// item can't be previewed (e.g. poll-only items have no message).
		previewTarget() {
			const chatId = this.message?.chat_id ?? this.item.poll?.chat_id ?? null
			const messageId = this.message?.id ?? null
			if (!chatId || !messageId) return null
			return { chatId: Number(chatId), messageId: Number(messageId) }
		},

		isDeleted() {
			return !!this.message?.deleted_at
		},

		hasText() {
			return this.item.type === 'message' || this.item.type === 'reply'
		},

		wordCount() {
			if (!this.hasText) return 0
			const text = (this.message?.text || '').trim()
			if (!text) return 0
			return text.split(/\s+/).filter(Boolean).length
		},

		chatUrl() {
			const linkChatId =
				this.message?.chat_id ?? this.item.poll?.chat_id ?? null
			if (!linkChatId) return null
			const messageId = this.message?.id ?? null
			return this.$router.resolve({
				name: 'chat',
				params: { chat: 'chat', id: String(linkChatId) },
				query: messageId ? { centered: messageId } : {},
			}).href
		},

		showFooter() {
			return this.wordCount > 0 || (!!this.chatUrl && !this.isDeleted)
		},

		threadName() {
			const messageChatId = this.message?.chat_id
			if (!messageChatId) return null
			if (String(messageChatId) === String(this.chatId)) return null
			const chat = getChat(messageChatId)
			if (!chat?.is_thread) return null
			return chat.title || chat.displayname || null
		},

		formattedTime() {
			return this.$m(this.item.occurred_at).format('lll')
		},
	},
}
</script>

<style lang="scss" scoped>
@mixin border-fade($accent) {
	border-left-color: $accent;
	&:hover {
		border-left-color: color-mix(in srgb, $accent 40%, transparent);
	}
}

.activity-card {
	background: var(--neutral-0);
	border-radius: 8px;
	border: 1px solid var(--neutral-200);
	border-left-width: 4px;
	padding: 20px 24px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	transition: box-shadow 0.15s ease;

	&.clickable {
		cursor: pointer;

		&:hover {
			box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.06),
				0 2px 8px 0 rgba(0, 0, 0, 0.08);
		}
	}

	&.type-message {
		@include border-fade(var(--interaction-message-accent));
	}
	&.type-reply {
		@include border-fade(var(--interaction-reply-accent));
	}
	&.type-reaction {
		@include border-fade(var(--interaction-reaction-accent));
	}
	&.type-poll_response {
		@include border-fade(var(--teal-300));
	}
	&.type-chat_event {
		@include border-fade(var(--interaction-event-accent));
	}
}

.activity-card-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;

	.pills {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}
}

.activity-timestamp {
	font-family: Avenir-Roman;
	font-size: 14px;
	line-height: 20px;
	color: var(--neutral-500);
	white-space: nowrap;
}

// MessagePreview disables all pointer-events; re-enable on interactive bits
// (translation globe, mentions) so admins can click them in the activity feed.
.activity-card-body :deep(.message-preview .pointer) {
	pointer-events: auto;
}

// Re-enable media clicks so photos/videos/files open the lightbox modal, just
// like the normal message view. Targets MessageMedia's stable root wrapper.
.activity-card-body :deep(.message-media-container) {
	pointer-events: auto;
	// VisualMedia/MediaFile size themselves to a fixed pixel cap
	// (IMAGE_MAX_WIDTH, 400px), which can be wider than the activity card —
	// especially with the preview pane open. Clamp media to the card so nothing
	// overflows the container.
	max-width: 100%;

	.pronto-visual-media,
	.media-grid-container,
	.media-file-container {
		max-width: 100%;
	}
}

.activity-card-footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	font-family: Avenir-Roman;
	font-size: 14px;
	line-height: 20px;
	color: var(--neutral-500);
	// No word-count / no divider: the row is just the hover-only "Show in
	// chat" link, so a negative top margin pulls it closer to the body and
	// keeps the bottom of the card from looking like dead whitespace.
	margin-top: -8px;

	// When the divider is present, balance the gap above the content with
	// the card's bottom padding so the footer row sits vertically centered
	// in the space between the divider line and the card bottom.
	&.has-text {
		justify-content: space-between;
		border-top: 1px solid var(--neutral-200);
		padding-top: 20px;
		margin-top: 0;
	}

	.show-in-chat {
		font-family: Avenir-Medium;
		font-size: 14px;
		line-height: 20px;
		color: var(--blue-400);
		text-decoration: none;
		cursor: pointer;
		opacity: 0;
		transition: opacity 0.15s ease;

		&:hover {
			text-decoration: underline;
		}
	}
}

.activity-card:hover .activity-card-footer .show-in-chat {
	opacity: 1;
}
</style>
